'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'

export type ChecklistTask = {
  id: string
  title: string
  why: string
  group: string
  actionType: 'link' | 'guided' | 'info'
  actionTarget: string
  flagship: boolean
}

const GROUP_ORDER: { key: string; label: string }[] = [
  { key: 'this-week', label: 'This week' },
  { key: 'first-month', label: 'First month' },
  { key: 'first-90-days', label: 'First 90 days' },
]

export function Checklist({ tasks, subdomain }: { tasks: ChecklistTask[]; subdomain: string }) {
  const storageKey = `bst-onboarding:${subdomain}`
  const [done, setDone] = useState<Record<string, boolean>>({})
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) setDone(JSON.parse(raw))
    } catch {
      /* ignore */
    }
    setLoaded(true)
  }, [storageKey])

  const toggle = (id: string) => {
    setDone((prev) => {
      const next = { ...prev, [id]: !prev[id] }
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }

  const doneCount = useMemo(() => tasks.filter((t) => done[t.id]).length, [tasks, done])
  const pct = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0

  const grouped = GROUP_ORDER.map((g) => ({
    ...g,
    items: tasks.filter((t) => t.group === g.key),
  })).filter((g) => g.items.length > 0)

  return (
    <div>
      <div className="progress-wrap">
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${loaded ? pct : 0}%` }} />
        </div>
        <div className="progress-label">
          {loaded ? `You're ${doneCount} of ${tasks.length} steps in (${pct}%)` : ' '}
        </div>
      </div>

      {grouped.map((g) => (
        <div className="checklist-group" key={g.key}>
          <h3>{g.label}</h3>
          {g.items.map((t) => {
            const isDone = Boolean(done[t.id])
            return (
              <div className={`check-item${isDone ? ' done' : ''}`} key={t.id}>
                <button
                  className="check-toggle"
                  aria-pressed={isDone}
                  aria-label={isDone ? 'Mark not done' : 'Mark done'}
                  onClick={() => toggle(t.id)}
                >
                  {isDone ? '✓' : ''}
                </button>
                <div className="check-body">
                  <div className="check-title">
                    {t.flagship ? <span className="flag">★ </span> : null}
                    {t.title}
                  </div>
                  {t.why ? <p className="check-why">{t.why}</p> : null}
                  {t.actionTarget && t.actionType !== 'info' ? (
                    t.actionTarget.startsWith('/') ? (
                      <Link className="check-action" href={t.actionTarget}>
                        {t.actionType === 'guided' ? 'Start guided setup →' : 'Open →'}
                      </Link>
                    ) : (
                      <a
                        className="check-action"
                        href={t.actionTarget}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open →
                      </a>
                    )
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
